import { useCallback, useEffect, useState } from 'react';
import {
  ArrowRightLeftIcon,
  ChevronRightIcon,
  ClipboardListIcon,
  DatabaseIcon,
  ListChecksIcon,
  MessageSquareXIcon,
  ShieldAlertIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { bridge, task as taskApi, track, type TaskSnapshot, type TaskStatus } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAppReady } from '@/lib/useAppReady';
import { PageShell } from '@/components/PageShell';
import { ProgressBar, taskStatusLabel } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RowList } from '@/components/RowList';
import { cn } from '@/lib/utils';
import { SectionHeader } from '@/components/SectionHeader';
import { CARD, MUTED, NUM } from '@/components/ui';

/**
 * 任务中心。结构照搬搬小书 `xhshub/apps/web/src/app/mobile/tools/tasks/page.tsx`：
 * 统计卡 + 进行中列表 + 已结束列表 + 行内操作（按状态决定）。
 */

type TaskAction = 'pause' | 'resume' | 'delete';

const ACTION_LABELS: Record<TaskAction, string> = {
  pause: '暂停',
  resume: '继续',
  delete: '删除',
};

/** 每个状态可做的操作。迁移任务可删；备份任务的游标要留着续跑，所以不给删除。 */
const ACTIONS_BY_STATUS: Record<TaskStatus, TaskAction[]> = {
  idle: ['delete'],
  pending: ['delete'],
  running: ['pause'],
  paused: ['resume', 'delete'],
  completed: ['delete'],
  failed: ['resume', 'delete'],
  cancelled: ['delete'],
};

const PAUSE_REASON_LABELS: Record<string, string> = {
  login_expired: '登录已过期，请重新登录后继续',
  account_banned: '账号被封禁',
  rate_limited: '被限流，稍后会自动继续',
  risk: '需要完成风控验证',
};

function isActive(status: TaskStatus): boolean {
  return status === 'running' || status === 'paused' || status === 'idle' || status === 'pending';
}

function actionsFor(taskItem: TaskSnapshot): TaskAction[] {
  const base = ACTIONS_BY_STATUS[taskItem.status] ?? [];
  return taskItem.type === 'migration' ? base : base.filter((a) => a !== 'delete');
}

function statusTone(status: TaskStatus): string {
  if (status === 'running') return 'text-foreground font-medium';
  if (status === 'failed') return 'text-destructive';
  return 'text-muted-foreground';
}

function typeIcon(type: string) {
  if (type === 'migration') return <ArrowRightLeftIcon className="size-4" />;
  if (type === 'backup') return <DatabaseIcon className="size-4" />;
  if (type === 'post_action' || type === 'visibility') return <ListChecksIcon className="size-4" />;
  if (type === 'comment_clean') return <MessageSquareXIcon className="size-4" />;
  return <ClipboardListIcon className="size-4" />;
}

function formatTime(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function TaskRow({
  task: taskItem,
  busy,
  onAction,
  base,
}: {
  task: TaskSnapshot;
  busy: boolean;
  onAction: (action: TaskAction) => void;
  /** 路由前缀：`/bxy` 或 `/shushu` */
  base: string;
}) {
  const navigate = useNavigate();
  const active = isActive(taskItem.status);

  /// 点行进任务详情页（详情页里有「打开控制台」去对应页面）。
  const openDetail = () => navigate(`${base}/tasks/${encodeURIComponent(taskItem.taskId)}`);
  const actions = actionsFor(taskItem);
  const total = taskItem.total ?? 0;
  const current = taskItem.current ?? 0;
  const meta = [
    taskItem.accountNickname || null,
    total > 0 ? `${current} / ${total}` : '等待统计',
    (taskItem.failedCount ?? 0) > 0 ? `失败 ${taskItem.failedCount}` : null,
    formatTime(taskItem.createdAt),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      data-haptic
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') openDetail();
      }}
      className="bg-muted/60 active:bg-accent flex cursor-pointer items-start gap-3 rounded-control px-3.5 py-3 transition-colors ios:rounded-none ios:bg-transparent ios:px-0 ios:active:bg-transparent ios:active:opacity-60"
    >
      <span className="bg-background text-foreground flex size-9 shrink-0 items-center justify-center rounded-full">
        {typeIcon(taskItem.type)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <strong className="text-subheadline truncate font-semibold ios:text-body">{taskItem.name}</strong>
          <span className={`text-caption1 shrink-0 ios:text-footnote ${statusTone(taskItem.status)}`}>
            {taskStatusLabel(taskItem.status, { idle: '排队中' }) ?? taskItem.status}
          </span>
        </div>
        <p className={`${MUTED} mt-0.5 ${NUM}`}>{meta}</p>
        {active && total > 0 ? <ProgressBar ratio={current / total} /> : null}
        {taskItem.pauseReason ? (
          <p className="text-footnote mt-1 text-muted-foreground">
            {PAUSE_REASON_LABELS[taskItem.pauseReason] ?? '任务已暂停'}
          </p>
        ) : null}
        {taskItem.error ? (
          <p className="text-footnote text-destructive mt-1">
            {describeError(taskItem.error)}
          </p>
        ) : null}
        {actions.length > 0 ? (
          // iOS 下删除只放在详情页（原生习惯：破坏性操作不堆在列表行上）
          <div className={cn('mt-2 flex justify-end gap-2', actions.every((a) => a === 'delete') && 'ios:hidden')}>
            {actions.map((a) => (
              <Button
                key={a}
                size="sm"
                variant={a === 'delete' ? 'ghost' : 'tinted'}
                loading={busy}
                className={a === 'delete' ? 'text-destructive hover:bg-destructive/10 ios:hidden' : ''}
                onClick={(e) => {
                  e.stopPropagation();
                  onAction(a);
                }}
              >
                {ACTION_LABELS[a]}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
      {/* iOS 列表行的「可进入」指示 */}
      <ChevronRightIcon className="text-muted-foreground/50 hidden size-4 shrink-0 self-center ios:block" />
    </div>
  );
}

/** 任务中心。搬小音与薯薯共用（`task.*` 形状一致），`app` 决定路由前缀与页面描述。 */
export function TasksPage({ app = 'bxy' }: { app?: 'bxy' | 'shushu' }) {
  const base = `/${app}`;
  const [tasks, setTasks] = useState<TaskSnapshot[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { tasks: next } = await taskApi.list();
      setTasks(next);
      setError(null);
    } catch (e) {
      setError(describeError(e));
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoaded(true);
    })();
  }, [refresh]);

  // 任务表变化由原生推 task.changed，不轮询（PROTOCOL.md §5）
  useEffect(() => {
    const off = bridge.on('task.changed', () => {
      void refresh();
    });
    return off;
  }, [refresh]);

  const runAction = useCallback(
    async (taskItem: TaskSnapshot, action: TaskAction) => {
      setBusyId(taskItem.taskId);
      setError(null);
      try {
        const next =
          action === 'pause'
            ? await taskApi.pause(taskItem.taskId)
            : action === 'resume'
              ? await taskApi.resume(taskItem.taskId)
              : await taskApi.delete(taskItem.taskId);
        setTasks(next.tasks);
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  const active = tasks.filter((t) => isActive(t.status));
  const finished = tasks.filter((t) => !isActive(t.status));

  useAppReady(loaded);

  // 埋点：页面访问
  useEffect(() => {
    track.pageOpen('tasks');
  }, []);

  return (
    <PageShell
      title="任务中心"
      description={app === 'shushu' ? '备份、迁移与批量操作的进度和历史' : '搬小音 · 由原生 BridgeWebView 承载'}
      backTo={app === 'shushu' ? undefined : '/'}
    >
      {tasks.length > 0 ? (
        // iOS 下各分组标题已带数量，不再单独放统计卡
        <Card className={cn(CARD, 'ios:hidden')}>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/60 rounded-icon px-3 py-2.5">
              <p className="text-caption1 text-muted-foreground">进行中</p>
              <p className={`text-title3 mt-0.5 font-semibold ${NUM}`}>{active.length}</p>
            </div>
            <div className="bg-muted/60 rounded-icon px-3 py-2.5">
              <p className="text-caption1 text-muted-foreground">已结束</p>
              <p className={`text-title3 mt-0.5 font-semibold ${NUM}`}>{finished.length}</p>
            </div>
          </div>
        </Card>
      ) : null}

      {loaded && tasks.length === 0 && !error ? (
        <Card className={CARD}>
          <div className="flex items-center gap-2">
            <ShieldAlertIcon className="text-primary size-5 shrink-0" />
            <strong className="text-callout font-semibold">还没有任务</strong>
          </div>
          <p className={`${MUTED} mt-1`}>
            在「备份控制台」或「迁移控制台」开始一个任务后，会出现在这里。
          </p>
        </Card>
      ) : null}

      {active.length > 0 ? (
        <Card className={CARD}>
          <SectionHeader title="进行中" trailing={<span className="hidden ios:inline">{active.length} 个</span>} />
          <RowList>
            {active.map((t) => (
              <TaskRow base={base}
                key={t.taskId}
                task={t}
                busy={busyId === t.taskId}
                onAction={(a) => void runAction(t, a)}
              />
            ))}
          </RowList>
        </Card>
      ) : null}

      {finished.length > 0 ? (
        <Card className={CARD}>
          <SectionHeader title="已结束" trailing={<span className="hidden ios:inline">{finished.length} 个</span>} />
          <RowList>
            {finished.map((t) => (
              <TaskRow base={base}
                key={t.taskId}
                task={t}
                busy={busyId === t.taskId}
                onAction={(a) => void runAction(t, a)}
              />
            ))}
          </RowList>
        </Card>
      ) : null}

      {error ? <p className="text-destructive px-1 text-sm">{error}</p> : null}

      {!loaded ? <PageSkeleton kind="tasks" /> : null}
    </PageShell>
  );
}
